import { useState } from 'react';
import {
    BodySmallEmphasized,
    BodySmallRegular,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { useGetTrace } from '../hooks/useGetTrace';
import { useListTraces } from '../hooks/useListTraces';
import { SpanDetailPanel } from './SpanDetailPanel';
import { TraceDetailView } from './TraceDetailView';

const SPAN_TYPE_COLORS: Record<string, string> = {
    agent_run: '#2563eb',
    llm_call: '#16a34a',
    tool_call: '#9333ea',
    mcp_tool_call: '#9333ea',
    memory_operation: '#ea580c',
    processor_run: '#475569',
    workflow_run: '#2563eb',
    workflow_step: '#2563eb',
    generic: '#475569',
};

const SPAN_TYPE_LABELS: Record<string, string> = {
    agent_run: 'Agent',
    llm_call: 'Model',
    tool_call: 'Tool',
    mcp_tool_call: 'Tool',
    memory_operation: 'Memory',
    processor_run: 'Other',
    workflow_run: 'Agent',
    workflow_step: 'Agent',
    generic: 'Other',
};

const getSpanColor = (spanType?: string): string =>
    SPAN_TYPE_COLORS[spanType ?? 'generic'] ?? '#475569';

export const ThreadTracesPanel = ({ threadId }: { threadId: string }) => {
    const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);

    const {
        data: tracesResponse,
        isLoading,
        error,
    } = useListTraces({
        threadId,
    });

    const traces = tracesResponse?.spans ?? [];

    if (isLoading) {
        return <Spinner />;
    }

    if (error != null) {
        return (
            <BodySmallRegular color="$red1100">
                Unable to load traces: {error.message}
            </BodySmallRegular>
        );
    }

    if (traces.length === 0) {
        return (
            <BodySmallRegular color="$gray1000">
                No traces found for this thread.
            </BodySmallRegular>
        );
    }

    return (
        <XStack flex={1} gap={0} minHeight={600}>
            <YStack
                width={420}
                flexShrink={0}
                borderRightWidth={1}
                borderColor="$gray300"
            >
                <YStack
                    paddingVertical={10}
                    paddingHorizontal={14}
                    backgroundColor="white"
                    borderBottomWidth={1}
                    borderColor="$gray300"
                >
                    <BodySmallEmphasized color="$gray1200">
                        Traces
                    </BodySmallEmphasized>
                </YStack>
                <YStack
                    paddingVertical={6}
                    paddingHorizontal={14}
                    backgroundColor="$gray100"
                    borderBottomWidth={1}
                    borderColor="$gray300"
                >
                    <BodySmallRegular color="$gray1100">
                        THREAD # {threadId.slice(0, 24)}... ({traces.length})
                    </BodySmallRegular>
                </YStack>
                <YStack flex={1} overflow="auto">
                    {traces.map((span) => (
                        <TraceListRow
                            key={span.traceId}
                            span={span}
                            isSelected={selectedTraceId === span.traceId}
                            onSelect={() => {
                                setSelectedTraceId(span.traceId);
                            }}
                        />
                    ))}
                </YStack>
            </YStack>

            <YStack flex={1} overflow="auto">
                {selectedTraceId != null ? (
                    <TraceDetailPanel traceId={selectedTraceId} />
                ) : (
                    <YStack
                        flex={1}
                        justifyContent="center"
                        alignItems="center"
                        padding={32}
                    >
                        <BodySmallRegular color="$gray1000">
                            Select a trace to view details
                        </BodySmallRegular>
                    </YStack>
                )}
            </YStack>
        </XStack>
    );
};

const TraceListRow = ({
    span,
    isSelected,
    onSelect,
}: {
    span: {
        traceId: string;
        name?: string;
        startedAt?: string | Date | null;
        input?: unknown;
        entityType?: string | null;
        entityName?: string | null;
    };
    isSelected: boolean;
    onSelect: () => void;
}) => {
    const date = span.startedAt != null ? new Date(span.startedAt) : null;
    const inputPreview = getInputPreview(span.input);

    return (
        <XStack
            paddingVertical={10}
            paddingHorizontal={14}
            gap={12}
            cursor="pointer"
            backgroundColor={isSelected ? '$green300' : 'white'}
            hoverStyle={{
                backgroundColor: isSelected ? '$green300' : '$gray100',
            }}
            borderBottomWidth={1}
            borderColor="$gray200"
            onPress={onSelect}
            alignItems="center"
        >
            <BodySmallRegular
                color={isSelected ? '$green1100' : '$gray1200'}
                width={64}
                numberOfLines={1}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
            >
                {span.traceId.slice(0, 7)}
            </BodySmallRegular>
            <BodySmallRegular
                color={isSelected ? '$green1100' : '$gray1100'}
                width={90}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
            >
                {date != null
                    ? date.toLocaleTimeString([], {
                          hour12: false,
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                      })
                    : '-'}
            </BodySmallRegular>
            <BodySmallRegular
                color={isSelected ? '$green1100' : '$gray1200'}
                flex={1}
                numberOfLines={1}
            >
                {inputPreview}
            </BodySmallRegular>
            <BodySmallRegular
                color={isSelected ? '$green1100' : '$gray1000'}
                width={70}
                numberOfLines={1}
                textAlign="right"
            >
                {span.entityName ?? span.entityType ?? '-'}
            </BodySmallRegular>
        </XStack>
    );
};

const TraceDetailPanel = ({ traceId }: { traceId: string }) => {
    const { data: trace, isLoading } = useGetTrace({ traceId });
    const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);

    if (isLoading) {
        return (
            <YStack flex={1} justifyContent="center" alignItems="center">
                <Spinner />
            </YStack>
        );
    }

    if (trace == null) {
        return (
            <YStack padding={16}>
                <BodySmallRegular color="$red1100">
                    Trace not found.
                </BodySmallRegular>
            </YStack>
        );
    }

    const spans = trace.spans;
    const rootSpan = spans.find(
        (s: { parentSpanId?: string | null }) => s.parentSpanId == null,
    );
    const duration =
        rootSpan?.startedAt != null && rootSpan.endedAt != null
            ? new Date(rootSpan.endedAt).getTime() -
              new Date(rootSpan.startedAt).getTime()
            : null;
    const hasError = spans.some((s: { error?: unknown }) => s.error != null);

    const selectedSpan =
        selectedSpanId != null
            ? spans.find(
                  (s: { spanId?: string }) => s.spanId === selectedSpanId,
              )
            : null;

    return (
        <YStack flex={1} gap={0}>
            <TraceDetailHeader
                traceId={traceId}
                rootSpan={rootSpan}
                duration={duration}
                hasError={hasError}
            />

            <SpanTypeLegend spans={spans} />

            <XStack flex={1} gap={0}>
                <YStack flex={1} padding={16} overflow="auto">
                    <TraceDetailView
                        spans={spans}
                        selectedSpanId={selectedSpanId}
                        onSelectSpan={setSelectedSpanId}
                    />
                </YStack>

                {selectedSpan != null && (
                    <YStack
                        width={400}
                        borderLeftWidth={1}
                        borderColor="$gray300"
                        overflow="auto"
                    >
                        <SpanDetailPanel
                            span={selectedSpan}
                            onClose={() => {
                                setSelectedSpanId(null);
                            }}
                        />
                    </YStack>
                )}
            </XStack>
        </YStack>
    );
};

const TraceDetailHeader = ({
    traceId,
    rootSpan,
    duration,
    hasError,
}: {
    traceId: string;
    rootSpan?: {
        entityName?: string | null;
        entityType?: string | null;
        startedAt?: string | Date | null;
        endedAt?: string | Date | null;
    };
    duration: number | null;
    hasError: boolean;
}) => (
    <YStack
        padding={20}
        borderBottomWidth={1}
        borderColor="$gray300"
        backgroundColor="white"
        gap={16}
    >
        <XStack gap={8} alignItems="center">
            <BodySmallRegular color="$gray1000">Trace</BodySmallRegular>
            <BodySmallEmphasized
                color="$gray1200"
                style={{ fontFamily: 'monospace', fontSize: 13 }}
            >
                # {traceId}
            </BodySmallEmphasized>
        </XStack>

        <XStack gap={32} flexWrap="wrap">
            <HeaderField
                label="Entity Id"
                value={rootSpan?.entityName ?? '-'}
            />
            <HeaderField
                label="Entity Type"
                value={rootSpan?.entityType ?? '-'}
            />
            <HeaderField
                label="Status"
                value={hasError ? 'Error' : 'Success'}
                color={hasError ? '$red1100' : '$green900'}
            />
            <HeaderField
                label="Duration"
                value={duration != null ? formatDuration(duration) : '-'}
            />
            <HeaderField
                label="Started at"
                value={
                    rootSpan?.startedAt != null
                        ? new Date(rootSpan.startedAt).toLocaleString()
                        : '-'
                }
            />
            <HeaderField
                label="Ended at"
                value={
                    rootSpan?.endedAt != null
                        ? new Date(rootSpan.endedAt).toLocaleString()
                        : '-'
                }
            />
        </XStack>
    </YStack>
);

const HeaderField = ({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) => (
    <YStack gap={4}>
        <BodySmallRegular color="$gray1000">{label}</BodySmallRegular>
        <BodySmallEmphasized color={color ?? '$gray1200'}>
            {value}
        </BodySmallEmphasized>
    </YStack>
);

const SpanTypeLegend = ({ spans }: { spans: Array<{ spanType?: string }> }) => {
    const typeCounts = new Map<string, number>();
    for (const span of spans) {
        const label = SPAN_TYPE_LABELS[span.spanType ?? 'generic'] ?? 'Other';
        typeCounts.set(label, (typeCounts.get(label) ?? 0) + 1);
    }

    const legendItems = Array.from(typeCounts.entries()).map(([label]) => {
        const spanType = Object.entries(SPAN_TYPE_LABELS).find(
            ([, v]) => v === label,
        )?.[0];
        return {
            label,
            color: getSpanColor(spanType),
        };
    });

    return (
        <XStack
            paddingVertical={10}
            paddingHorizontal={20}
            gap={20}
            borderBottomWidth={1}
            borderColor="$gray300"
            backgroundColor="white"
            alignItems="center"
        >
            {legendItems.map((item) => (
                <XStack key={item.label} gap={8} alignItems="center">
                    <YStack
                        width={10}
                        height={10}
                        borderRadius={5}
                        backgroundColor={item.color}
                    />
                    <BodySmallRegular color="$gray1200">
                        {item.label}
                    </BodySmallRegular>
                </XStack>
            ))}
        </XStack>
    );
};

function getInputPreview(input: unknown): string {
    if (input == null) return '-';
    if (typeof input === 'string') return input.slice(0, 40);
    try {
        const str = JSON.stringify(input);
        return str.slice(0, 40) + (str.length > 40 ? '...' : '');
    } catch {
        return '-';
    }
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
}
